import get from 'lodash/get';
import api from 'services/api';
import storefrontsActions from 'actions/storefronts';
import appsActions from 'actions/apps';
import { dataSlice } from './slices/data';
import { editorSlice } from './slices/editor';
import {
  cloneBlock,
  cloneSection,
  collectDirtyTasks,
  createBlock,
  createSection,
  findPathById,
  generateId,
  getBlockSchema,
  getInsertionTarget,
  getSectionSchema,
  localesFromConfigs,
  matchPage,
  pageHasRecord,
  parseConfigs,
  parsePath,
} from './utils';

const CONFIG_FETCH_OPTIONS = Object.freeze({
  fields: 'name, file, file_path, version, hash',
  include: {
    file_data: {
      url: '/apps:configs/{id}/file/data',
    },
  },
});

const PAGE_RECORD_QUERY = Object.freeze({
  active: { $ne: false },
  fields: ['id', 'name', 'slug', 'number', 'title', 'theme_template'],
  limit: 5,
});

const pendingSaves = new Map();

const taskKey = (task) =>
  task.pageId ? `${task.group}:${task.pageId}` : task.group;

function getAppId(getState) {
  return getState().storefronts.storefront?.app_id ?? null;
}

function dispatchAddAndSelect(dispatch, addAction, selectedPath) {
  dispatch(addAction);
  dispatch(editorSlice.actions.setSelectedPath(selectedPath));
}

function runSaveTask(dispatch, appId, task) {
  const key = taskKey(task);

  const promise = dispatch(
    actions.updateConfig(appId, task.configId, {
      file: { data: JSON.stringify(task.fileData) },
    }),
  )
    .then((updated) => {
      if (updated?.id) {
        dispatch(editorSlice.actions.replaceConfig(updated));
      }
    })
    .finally(() => {
      pendingSaves.delete(key);
      if (pendingSaves.size === 0) {
        dispatch(editorSlice.actions.setSaving(false));
      }
    });

  pendingSaves.set(key, promise);
  return promise;
}

function resolvePageRecord(page, recordId) {
  if (!pageHasRecord(page)) return null;
  if (recordId) {
    return api.get(`/data/${page.collection}/:first`, {
      ...PAGE_RECORD_QUERY,
      $or: [{ id: recordId }, { slug: recordId }],
    });
  }
  return api.get(
    `/data/${page.collection}/${recordId ?? ':last'}`,
    PAGE_RECORD_QUERY,
  );
}

// Persist one locale config from the language modal: update by id, or create a
// new file when the locale has none yet. Returns whether it was saved.
async function saveLocaleConfig(dispatch, appId, config) {
  const file = { data: config.file_data };

  if (config.id) {
    const updated = await dispatch(
      actions.updateConfig(appId, config.id, { file }),
    );
    if (!updated?.id) {
      return false;
    }
    dispatch(editorSlice.actions.replaceConfig(updated));
    return true;
  }

  const created = await dispatch(
    appsActions.createConfig(appId, { file_path: config.file_path, file }),
  );
  if (!created?.id) {
    return false;
  }
  const full = await dispatch(actions.fetchConfig(appId, created.id));
  dispatch(editorSlice.actions.replaceConfig(full ?? created));
  return true;
}

const actions = {
  fetchConfigs: (appId) => () =>
    api.getAll(`/apps/${appId}/configs`, {
      file_path: { $regex: 'frontend/theme' },
      ...CONFIG_FETCH_OPTIONS,
    }),

  fetchConfig: (appId, configId) => () =>
    api.get(`/apps/${appId}/configs/${configId}`, CONFIG_FETCH_OPTIONS),

  updateConfig: (appId, configId, data) => async (dispatch) => {
    const updated = await api.put(`/apps/${appId}/configs/${configId}`, data);
    if (!updated?.id) return null;
    return dispatch(actions.fetchConfig(appId, updated.id));
  },

  setSetupError: (message) => (dispatch) =>
    dispatch(editorSlice.actions.setSetupError(message)),

  setupEditor:
    ({ storefrontId }) =>
    async (dispatch) => {
      const storefront = await dispatch(storefrontsActions.fetch(storefrontId));
      if (!storefront?.app_id) {
        dispatch(
          editorSlice.actions.setSetupError(
            'Storefront has no associated app.',
          ),
        );
        return;
      }

      const [configsResult, appResult] = await Promise.all([
        dispatch(actions.fetchConfigs(storefront.app_id)),
        dispatch(appsActions.fetch(storefront.app_id)),
        dispatch(storefrontsActions.fetchMenus(storefront.id)),
      ]);

      const configs = configsResult?.results;
      if (!configs?.length) {
        dispatch(
          editorSlice.actions.setSetupError(
            'Unable to load theme editor configuration.',
          ),
        );
        return;
      }

      const pages = appResult?.storefront?.theme?.pages ?? [];
      const parsed = parseConfigs(configs);

      dispatch(
        editorSlice.actions.setup({
          configs,
          pages,
          pageContents: parsed.pageContents,
          locales: parsed.locales,
        }),
      );
      dispatch(
        dataSlice.actions.setData({
          header: parsed.header,
          footer: parsed.footer,
          settings: parsed.settings,
        }),
      );

      return { storefront, app: appResult };
    },

  selectPage:
    ({ pageId, recordId }) =>
    async (dispatch, getState) => {
      const initialState = getState().storefrontEditor;
      const currentRecord = initialState.pageRecord;
      const requestedRecord = recordId ?? null;
      const recordMatches = requestedRecord
        ? currentRecord?.id === requestedRecord ||
          currentRecord?.slug === requestedRecord
        : !currentRecord;

      if (initialState.page?.id === pageId && recordMatches) {
        return;
      }

      const page =
        initialState.pages.find((p) => p.id === pageId) ??
        initialState.pages[0];
      if (!page) {
        dispatch(editorSlice.actions.setError('No pages available.'));
        return;
      }

      if (initialState.page) {
        const current = initialState.data.present;
        dispatch(
          editorSlice.actions.setPageContent({
            pageId: initialState.page.id,
            content: current.content,
            pageMeta: current.pageMeta,
          }),
        );
      }

      const pageRecord = await resolvePageRecord(page, recordId);

      // Re-read after setPageContent may have written the current page's cache.
      const { pageContents } = getState().storefrontEditor;
      const target = pageContents[page.id] ?? {
        content: [],
        pageMeta: { title: '', description: '' },
      };

      dispatch(
        editorSlice.actions.loadPage({
          page,
          pageRecord,
          content: target.content,
          pageMeta: target.pageMeta,
        }),
      );

      dispatch(actions.save());
    },

  save: () => async (dispatch, getState) => {
    const state = getState().storefrontEditor;
    if (!state.canSave) return;

    const appId = getAppId(getState);
    if (!appId) return;

    const fresh = collectDirtyTasks(state).filter(
      (task) => !pendingSaves.has(taskKey(task)),
    );
    if (!fresh.length) return;

    dispatch(editorSlice.actions.setSaving(true));
    await Promise.all(fresh.map((task) => runSaveTask(dispatch, appId, task)));
  },

  // Receives the locale configs produced by the language modal:
  // `{ id?, file_path, file_data }[]` with already-stringified file_data.
  saveLocales: (configs) => async (dispatch, getState) => {
    const appId = getAppId(getState);
    if (!appId) return false;

    dispatch(editorSlice.actions.setSaving(true));
    try {
      const results = await Promise.all(
        configs.map((config) => saveLocaleConfig(dispatch, appId, config)),
      );

      const { locales } = getState().storefrontEditor;
      dispatch(
        editorSlice.actions.setLocales({
          ...locales,
          ...localesFromConfigs(configs),
        }),
      );

      return results.every(Boolean);
    } finally {
      dispatch(editorSlice.actions.setSaving(false));
    }
  },

  addSection:
    ({ path, type, index }) =>
    (dispatch, getState) => {
      const { schemas, data: undoableData } = getState().storefrontEditor;
      const sectionSchema = getSectionSchema(schemas, type);
      const sectionData = createSection(type, sectionSchema);
      if (!sectionData) return;

      const target = getInsertionTarget(undoableData.present, path, index);
      const section = { ...sectionData, id: generateId(type) };
      dispatchAddAndSelect(
        dispatch,
        dataSlice.actions.addSection({ path, section, index: target.index }),
        target.path,
      );
    },

  addBlock:
    ({ path, sectionType, type, index }) =>
    (dispatch, getState) => {
      const { schemas, data: undoableData } = getState().storefrontEditor;
      const blockSchema = getBlockSchema(schemas, sectionType, type);
      const blockData = createBlock(type, blockSchema);
      if (!blockData) return;

      const target = getInsertionTarget(undoableData.present, path, index);
      const block = { ...blockData, id: generateId(type) };
      dispatchAddAndSelect(
        dispatch,
        dataSlice.actions.addBlock({ path, block, index: target.index }),
        target.path,
      );
    },

  removeNode:
    ({ path }) =>
    (dispatch) => {
      dispatch(dataSlice.actions.removeNode({ path }));
      dispatch(editorSlice.actions.setSelectedPath(null));
      dispatch(editorSlice.actions.setHoveredPath(null));
    },

  duplicateNode:
    ({ path }) =>
    (dispatch, getState) => {
      const { data: undoableData } = getState().storefrontEditor;
      const node = get(undoableData.present, path);
      if (!node) return;

      const { isBlock, group, sectionIndex, slotId } = parsePath(path);

      if (isBlock) {
        const block = cloneBlock(node);
        const blockParentPath = `${group}.${sectionIndex}.blocks.${slotId}`;
        const target = getInsertionTarget(
          undoableData.present,
          blockParentPath,
        );
        dispatchAddAndSelect(
          dispatch,
          dataSlice.actions.addBlock({ path: blockParentPath, block }),
          target.path,
        );
      } else {
        const section = cloneSection(node);
        const target = getInsertionTarget(undoableData.present, group);
        dispatchAddAndSelect(
          dispatch,
          dataSlice.actions.addSection({ path: group, section }),
          target.path,
        );
      }
    },

  moveNode:
    ({ fromPath, toPath }) =>
    (dispatch, getState) => {
      const { selectedPath, hoveredPath } = getState().storefrontEditor;
      dispatch(dataSlice.actions.moveNode({ fromPath, toPath }));
      if (selectedPath === fromPath) {
        dispatch(editorSlice.actions.setSelectedPath(toPath));
      }
      if (hoveredPath === fromPath) {
        dispatch(editorSlice.actions.setHoveredPath(toPath));
      }
    },

  toggleNode:
    ({ path }) =>
    (dispatch, getState) => {
      const { data: undoableData } = getState().storefrontEditor;
      const node = get(undoableData.present, path);
      if (!node) return;

      const willBeEnabled = Boolean(node.disabled);

      dispatch(dataSlice.actions.toggleNode({ path }));
      dispatch(
        editorSlice.actions.setSelectedPath(willBeEnabled ? path : null),
      );
    },

  updateSetting: (payload) => (dispatch) => {
    dispatch(dataSlice.actions.updateSetting(payload));
  },

  setSelectedPath: (payload) => (dispatch) => {
    dispatch(editorSlice.actions.setSelectedPath(payload));
  },

  setHoveredPath: (payload) => (dispatch) => {
    dispatch(editorSlice.actions.setHoveredPath(payload));
  },

  // The iframe reports the hit node by id; resolve it to a path here.
  selectById: (id) => (dispatch, getState) => {
    const data = getState().storefrontEditor.data.present;
    dispatch(editorSlice.actions.setSelectedPath(findPathById(data, id)));
  },

  hoverById: (id) => (dispatch, getState) => {
    const data = getState().storefrontEditor.data.present;
    dispatch(editorSlice.actions.setHoveredPath(findPathById(data, id)));
  },

  setViewport: (payload) => (dispatch) => {
    dispatch(editorSlice.actions.setViewport(payload));
  },

  setCanSave: (payload) => (dispatch) => {
    dispatch(editorSlice.actions.setCanSave(payload));
  },

  setError: (payload) => (dispatch) => {
    dispatch(editorSlice.actions.setError(payload));
  },

  setPageRecord: (payload) => (dispatch) => {
    dispatch(editorSlice.actions.setPageRecord(payload));
  },

  appReady:
    ({ schemas }) =>
    (dispatch) => {
      dispatch(editorSlice.actions.appReady({ schemas }));
    },

  appRendered:
    ({ path }) =>
    (dispatch, getState) => {
      dispatch(editorSlice.actions.appRendered());

      if (!path) {
        return;
      }

      const { pages } = getState().storefrontEditor;
      const matched = matchPage(pages, path);
      if (matched) {
        dispatch(
          actions.selectPage({
            pageId: matched.page.id,
            recordId: matched.recordId,
          }),
        );
      }
    },
};

export { reducer } from './slices';
export default actions;
